// /lib/eventTracker.ts
//
// Backend delivery leg for behavioral analytics. GTMManager.pushEvent() calls
// trackBackendEvent() for every GTM event; a small whitelist maps GTM event
// names onto the backend's canonical event_type contract. Events are queued
// in memory and flushed in batches so we never block the UI or add per-event
// network overhead. Fire-and-forget: errors are swallowed, never surfaced.
//
// Guests are tracked too (Task 7): logged-out visitors get a backend-minted
// guest session (localStorage-cached uuid) and events flush with an
// `X-Session-Id` header instead of `Authorization`. When a guest later logs
// in, the stored session is linked (merged) into their account once and then
// discarded so any future logout starts a fresh guest session.
//
// Events that arrive before authStore finishes rehydrating (persisted token
// not yet trustworthy — see AuthInitializer's `isHydrated` flag) are held in
// a pre-hydration buffer rather than immediately tagged 'guest', so an
// authenticated cold load can never be misclassified as a guest and mint a
// real guest session. That buffer arms the same timer/listener as the main
// queue and gets a defensive drain on every flush() call (delivered as
// 'user' only when a token is provably present at that moment) so it isn't
// stranded by a tab teardown that happens before hydration finishes.
//
// SSR-safe: nothing at module scope touches window/document; all access is
// deferred to functions that only ever run client-side (GTMManager already
// bails out on `typeof window === "undefined"` before calling us).

import { useAuthStore } from "@/stores/authStore";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3001/v1/api";

const FLUSH_INTERVAL_MS = 5000;
const FLUSH_QUEUE_SIZE = 20;
const MAX_BATCH_SIZE = 50;

const GUEST_SESSION_STORAGE_KEY = "mila_guest_session_id";

export interface BackendEvent {
    event_type: string;
    entity_type?: string;
    entity_id?: string;
    metadata?: Record<string, any>;
    occurred_at: string;
}

interface GTMEventPayload {
    event: string;

    [key: string]: any;
}

/** GTM event name -> canonical backend event_type. Anything not listed is ignored. */
export const GTM_TO_BACKEND_EVENT: Record<string, string> = {
    view_item: "item_viewed",
    add_to_cart: "cart_item_added",
    add_to_cart_ai: "cart_item_added",
    remove_from_cart: "cart_item_removed",
    search: "search_performed",
    category_view: "category_viewed",
    reservation_page_view: "venue_viewed",
    // Venue add-to-cart fires this event (VenueBookingModal, right after
    // addVenue/replaceVenue), not add_to_cart - the ecommerce.items shape
    // add_to_cart/add_to_cart_ai use doesn't fit a venue booking's flat
    // venue_id/venue_name/vendor_id/vendor_name/physical_space_* payload.
    reservation_completed: "cart_item_added",
    app_open: "app_opened",

    // --- Discovery ---
    // `category_view` (CategoryViewTracker, fires on the category page) is the only
    // category signal mapped. `view_category` is deliberately ABSENT: its call sites pass
    // a homepage feature TILE ({key, title, image, bg, soon}) with no real category id,
    // so it can never satisfy category_viewed's required entity - mapping it would only
    // add dead mapper code.
    subcategory_view: "subcategory_viewed",
    // Vendor detail page. `external_restaurant_view` is the same page for vendors whose
    // booking is handled off-site, so it is the same behavioural signal.
    restaurant_view: "vendor_viewed",
    external_restaurant_view: "vendor_viewed",
    homepage_view: "homepage_viewed",
    select_item: "item_clicked",
    clicked_item: "item_clicked",
    filter_applied: "filter_applied",

    // --- Intent ---
    add_to_wishlist: "favorite_added",
    remove_from_wishlist: "favorite_removed",
    view_cart: "cart_viewed",
    reservation_started: "reservation_started",

    // --- Conversion ---
    begin_checkout: "checkout_started",
    // Planner storefront: the customer opened a payment link the planner sent.
    payment_link_open: "payment_link_opened",
    // Vendor referral links: a venue vendor's /book-external link opened with ?ref=<code>.
    referral_link_open: "referral_link_opened",
    add_payment_info: "payment_info_added",
    // `purchase` is deliberately absent - order_placed/payment_completed are emitted
    // server-side, where they are authoritative and cannot be spoofed or lost.

    // --- Account ---
    // GTM name is historical: `registration_form_complete` is pushed by GTMEvents.verifyOTP,
    // which in this OTP-based flow is what completes a LOGIN (see auth/signin/page.tsx and
    // components/auth/LoginModal.tsx). Renaming the GTM event would break existing GA
    // reports, so the mapping absorbs the mismatch instead.
    registration_form_complete: "user_logged_in",
    user_registered: "user_registered",
};

type EventMode = "user" | "guest";

interface QueuedEvent {
    event: BackendEvent;
    /** Auth mode at enqueue time — decides which header the event flushes with. */
    mode: EventMode;
    /**
     * Token captured at enqueue time for 'user'-tagged events only (undefined
     * for 'guest'). Flush can lag enqueue by up to FLUSH_INTERVAL_MS /
     * FLUSH_QUEUE_SIZE events; if a different user logs in during that
     * window, `token` at flush time would otherwise no longer belong to the
     * user who generated this event. flush() groups by this stamp and only
     * sends the group matching the CURRENT token, dropping the rest — so a
     * stale event can never be sent under a different account's Bearer
     * token (see flush()'s comment for the cross-account leak this closes).
     */
    userToken?: string;
}

let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let listenersInitialized = false;

// Pre-hydration buffer: authStore's `persist` middleware rehydrates
// asynchronously, and the codebase's established convention (8+ pages) is to
// treat `isHydrated` — not just a non-null `token` — as the trustworthy
// signal that auth state is settled (see AuthInitializer.tsx). Events that
// arrive before that flag flips hold here with no mode tag yet, so a cold
// load by an already-logged-in user can never be misclassified 'guest' (and
// never mints a real guest session) just because `token` was momentarily
// null on first render. Capped like the main queue so a pathological run of
// events before hydration can't grow this unboundedly; drop-oldest under
// pressure since the newest behavioral signal is more useful than the
// earliest once we're discarding something.
const PRE_HYDRATION_BUFFER_CAP = MAX_BATCH_SIZE;
let pendingPreHydrationEvents: BackendEvent[] = [];
let hydrationWatcherStarted = false;

// In-flight guest-session mint, shared by concurrent callers so we never
// double-mint (e.g. two rapid flushes both finding no stored session id).
let guestSessionMintPromise: Promise<string | null> | null = null;

// Login-link concurrency guard: the session id currently being linked (if
// any), plus a set of ids we've already linked so a stale caller can't retry
// one that already succeeded.
let linkInFlightSessionId: string | null = null;
const linkedSessionIds = new Set<string>();

function firstEcommerceItem(gtmEvent: GTMEventPayload): Record<string, any> | undefined {
    const items = gtmEvent?.ecommerce?.items;
    return Array.isArray(items) && items.length > 0 ? items[0] : undefined;
}

/**
 * The item-shaped metadata block shared by every ecommerce.items-based mapper case
 * (item_viewed, item_clicked, favorite_*, cart_item_*). One home for the snake/camel
 * vendor fallbacks and the localized-pair convention, so a new fallback key or name
 * field is added once instead of per case.
 *
 * Both languages where the call site has them - the admin event log must be searchable
 * in English and Arabic regardless of the locale the customer was browsing in. The
 * unsuffixed key stays as a fallback for call sites that only have the resolved name.
 */
function itemMetadata(item: Record<string, any> | undefined): Record<string, any> {
    return {
        item_name: item?.item_name,
        item_name_en: item?.item_name_en,
        item_name_ar: item?.item_name_ar,
        vendor_id: item?.vendor_id ?? item?.vendorId,
        vendor_name: item?.vendor_name ?? item?.vendorName,
        vendor_name_en: item?.vendor_name_en,
        vendor_name_ar: item?.vendor_name_ar,
    };
}

/** The flat venue-shaped metadata block shared by venue_viewed and reservation_started. */
function venueMetadata(gtmEvent: GTMEventPayload): Record<string, any> {
    return {
        venue_name: gtmEvent.venue_name,
        venue_name_en: gtmEvent.venue_name_en,
        venue_name_ar: gtmEvent.venue_name_ar,
        vendor_id: gtmEvent.vendor_id,
        vendor_name: gtmEvent.vendor_name,
        vendor_name_en: gtmEvent.vendor_name_en,
        vendor_name_ar: gtmEvent.vendor_name_ar,
        physical_space_id: gtmEvent.physical_space_id,
        physical_space_name: gtmEvent.physical_space_name,
        physical_space_name_en: gtmEvent.physical_space_name_en,
        physical_space_name_ar: gtmEvent.physical_space_name_ar,
    };
}

/** Map a whitelisted GTM event onto the backend entity contract. Returns null to drop the event. */
function mapToBackendEvent(
    backendType: string,
    gtmEvent: GTMEventPayload
): Omit<BackendEvent, "occurred_at"> | null {
    switch (backendType) {
        case "item_viewed": {
            const item = firstEcommerceItem(gtmEvent);
            const entityId = item?.item_id;
            if (!entityId) return null;
            return {
                event_type: backendType,
                entity_type: "item",
                entity_id: String(entityId),
                metadata: itemMetadata(item),
            };
        }

        case "venue_viewed": {
            const entityId = gtmEvent.venue_id;
            if (!entityId) return null;
            return {
                event_type: backendType,
                entity_type: "venue",
                entity_id: String(entityId),
                metadata: venueMetadata(gtmEvent),
            };
        }

        case "category_viewed": {
            const entityId = gtmEvent.category_id;
            if (!entityId) return null;
            return {
                event_type: backendType,
                entity_type: "category",
                entity_id: String(entityId),
                metadata: {
                    category_name: gtmEvent.category_name,
                    category_name_en: gtmEvent.category_name_en,
                    category_name_ar: gtmEvent.category_name_ar,
                    subcategory_id: gtmEvent.subcategory_id,
                },
            };
        }

        case "cart_item_added":
        case "cart_item_removed": {
            // Venue add-to-cart (reservation_completed) uses flat fields, not the
            // ecommerce.items shape add_to_cart/remove_from_cart use - this branch
            // is only ever reached for cart_item_added (see GTM_TO_BACKEND_EVENT,
            // there's no venue-add "removed" analog of this event name).
            if (gtmEvent.event === "reservation_completed") {
                const entityId = gtmEvent.venue_id;
                if (!entityId) return null;
                return {
                    event_type: backendType,
                    entity_type: "venue",
                    entity_id: String(entityId),
                    metadata: {
                        item_name: gtmEvent.venue_name,
                        quantity: 1,
                        price: gtmEvent.value,
                        vendor_id: gtmEvent.vendor_id,
                        vendor_name: gtmEvent.vendor_name,
                        physical_space_id: gtmEvent.physical_space_id,
                        physical_space_name: gtmEvent.physical_space_name,
                    },
                };
            }

            const item = firstEcommerceItem(gtmEvent);
            const entityId = item?.item_id;
            if (!entityId) return null;
            // Item add/remove calls never set this; the venue removal call site
            // tags its ecommerce item with entity_type: 'venue' so we can tell
            // the two apart from the shared GTMEvents.addToCart/removeFromCart shape.
            const entityType = item?.entity_type === "venue" ? "venue" : "item";
            const vendorId = item?.vendor_id ?? item?.vendorId;
            const vendorName = item?.vendor_name ?? item?.vendorName;
            const metadata: Record<string, any> = {
                item_name: item?.item_name,
                quantity: item?.quantity,
                vendor_id: vendorId,
                vendor_name: vendorName,
            };
            if (backendType === "cart_item_added") {
                metadata.price = item?.price;
            }
            if (entityType === "venue") {
                metadata.physical_space_id = item?.physical_space_id ?? item?.physicalSpaceId;
                metadata.physical_space_name = item?.physical_space_name ?? item?.physicalSpaceName;
            }
            return {
                event_type: backendType,
                entity_type: entityType,
                entity_id: String(entityId),
                metadata,
            };
        }

        case "search_performed": {
            return {
                event_type: backendType,
                metadata: {
                    query: gtmEvent.search_term,
                    result_count: gtmEvent.items_found,
                    search_type: gtmEvent.search_type || "general",
                },
            };
        }

        case "app_opened": {
            return {
                event_type: backendType,
                metadata: { platform: gtmEvent.platform || "web" },
            };
        }

        case "subcategory_viewed": {
            const entityId = gtmEvent.subcategory_id;
            if (!entityId) return null;
            return {
                event_type: backendType,
                entity_type: "subcategory",
                entity_id: String(entityId),
                metadata: {
                    subcategory_name_en: gtmEvent.subcategory_name_en,
                    subcategory_name_ar: gtmEvent.subcategory_name_ar,
                    category_id: gtmEvent.category_id,
                    category_name_en: gtmEvent.category_name_en,
                    category_name_ar: gtmEvent.category_name_ar,
                },
            };
        }

        case "payment_link_opened": {
            if (!gtmEvent.order_id) return null;
            return {
                event_type: backendType,
                entity_type: "order",
                entity_id: String(gtmEvent.order_id),
                metadata: { vendor_id: gtmEvent.vendor_id ? String(gtmEvent.vendor_id) : undefined, order_number: gtmEvent.order_number },
            };
        }

        case "referral_link_opened": {
            if (!gtmEvent.vendor_id) return null;
            return {
                event_type: backendType,
                entity_type: "vendor",
                entity_id: String(gtmEvent.vendor_id),
                metadata: { vendor_id: String(gtmEvent.vendor_id) },
            };
        }

        case "vendor_viewed": {
            // restaurant_view / external_restaurant_view both describe a vendor page.
            const entityId = gtmEvent.restaurant_id ?? gtmEvent.vendor_id;
            if (!entityId) return null;
            return {
                event_type: backendType,
                entity_type: "vendor",
                entity_id: String(entityId),
                metadata: {
                    vendor_id: String(entityId),
                    vendor_name: gtmEvent.restaurant_name ?? gtmEvent.vendor_name,
                    vendor_name_en: gtmEvent.vendor_name_en,
                    vendor_name_ar: gtmEvent.vendor_name_ar,
                },
            };
        }

        case "item_clicked": {
            const item = firstEcommerceItem(gtmEvent);
            const entityId = item?.item_id;
            if (!entityId) return null;
            return {
                event_type: backendType,
                entity_type: "item",
                entity_id: String(entityId),
                metadata: { ...itemMetadata(item), list_name: gtmEvent.item_list_name },
            };
        }

        case "favorite_added":
        case "favorite_removed": {
            const item = firstEcommerceItem(gtmEvent);
            const entityId = item?.item_id;
            if (!entityId) return null;
            // Favoriting is item-only today; the venue branch mirrors the cart
            // convention (call site tags entity_type: 'venue') in case that changes.
            const entityType = item?.entity_type === "venue" ? "venue" : "item";
            return {
                event_type: backendType,
                entity_type: entityType,
                entity_id: String(entityId),
                metadata: itemMetadata(item),
            };
        }

        case "reservation_started": {
            const entityId = gtmEvent.venue_id;
            if (!entityId) return null;
            return {
                event_type: backendType,
                entity_type: "venue",
                entity_id: String(entityId),
                metadata: venueMetadata(gtmEvent),
            };
        }

        case "cart_viewed":
        case "checkout_started": {
            const items = gtmEvent.ecommerce?.items;
            return {
                event_type: backendType,
                metadata: {
                    item_count: Array.isArray(items) ? items.length : undefined,
                    value: gtmEvent.ecommerce?.value,
                },
            };
        }

        case "payment_info_added": {
            return {
                event_type: backendType,
                metadata: {
                    payment_method: gtmEvent.payment_type ?? gtmEvent.ecommerce?.payment_type,
                    value: gtmEvent.ecommerce?.value,
                },
            };
        }

        case "filter_applied": {
            return {
                event_type: backendType,
                metadata: {
                    filter_name: gtmEvent.filter_name,
                    filter_value: gtmEvent.filter_value,
                },
            };
        }

        case "homepage_viewed": {
            return { event_type: backendType, metadata: {} };
        }

        case "user_logged_in":
        case "user_registered": {
            // The GTM payload carries the full user DTO. Deliberately NOT forwarded: it is
            // PII that the backend already knows from the authenticated request, and
            // metadata is surfaced verbatim in the admin payload inspector.
            return {
                event_type: backendType,
                metadata: { method: gtmEvent.method ?? "otp" },
            };
        }

        default:
            return null;
    }
}

function getStoredGuestSessionId(): string | null {
    if (typeof window === "undefined") return null;
    try {
        return window.localStorage.getItem(GUEST_SESSION_STORAGE_KEY);
    } catch {
        return null;
    }
}

function setStoredGuestSessionId(sessionId: string): void {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(GUEST_SESSION_STORAGE_KEY, sessionId);
    } catch {
        // Storage unavailable (private mode, quota, etc.) — session just
        // won't persist across reloads; not worth surfacing.
    }
}

function clearStoredGuestSessionId(): void {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.removeItem(GUEST_SESSION_STORAGE_KEY);
    } catch {
        // ignore
    }
}

/**
 * Returns a guest session id, minting one via POST /events/session if none
 * is stored yet. Concurrency-guarded so simultaneous callers share a single
 * in-flight mint request rather than double-minting. Never throws — returns
 * null on any failure (network error, non-2xx, malformed response), leaving
 * the caller to drop that flush.
 */
async function ensureGuestSession(): Promise<string | null> {
    if (typeof window === "undefined") return null;

    const stored = getStoredGuestSessionId();
    if (stored) return stored;

    if (guestSessionMintPromise) return guestSessionMintPromise;

    guestSessionMintPromise = (async () => {
        try {
            const res = await fetch(`${API_URL}/events/session`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ platform: "web" }),
            });
            if (!res.ok) return null;

            const body = await res.json();
            const sessionId: string | undefined = body?.data?.session_id;
            if (!sessionId) return null;

            setStoredGuestSessionId(sessionId);
            return sessionId;
        } catch {
            return null;
        } finally {
            guestSessionMintPromise = null;
        }
    })();

    return guestSessionMintPromise;
}

/**
 * If a guest session is on file and we now have a token, merges it into the
 * logged-in user via POST /events/session/link. Removes the stored session
 * id on success so a subsequent logout mints a fresh one. Fire-and-forget:
 * errors are swallowed and simply retried on the next call (next flush or
 * next auth-state change) since the session id stays stored until it works.
 * Concurrency-guarded per session id so overlapping callers (flush + the
 * AppOpenTracker token effect) never fire duplicate link requests.
 */
export function linkGuestSessionIfNeeded(token: string): void {
    if (typeof window === "undefined") return;
    if (!token) return;

    const sessionId = getStoredGuestSessionId();
    if (!sessionId) return;
    if (linkedSessionIds.has(sessionId)) return;
    if (linkInFlightSessionId === sessionId) return;

    linkInFlightSessionId = sessionId;

    fetch(`${API_URL}/events/session/link`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ session_id: sessionId }),
    })
        .then((res) => {
            if (res.ok) {
                linkedSessionIds.add(sessionId);
                clearStoredGuestSessionId();
            }
        })
        .catch(() => {})
        .finally(() => {
            if (linkInFlightSessionId === sessionId) {
                linkInFlightSessionId = null;
            }
        });
}

/**
 * Flushes anything buffered while auth state was still hydrating, tagging
 * each with the mode implied by the token available now that hydration is
 * done. No-op if nothing was buffered.
 */
function flushPreHydrationBuffer(): void {
    if (pendingPreHydrationEvents.length === 0) return;

    const { token } = useAuthStore.getState();
    const mode: EventMode = token ? "user" : "guest";

    const events = pendingPreHydrationEvents;
    pendingPreHydrationEvents = [];
    for (const event of events) {
        enqueue({ event, mode, userToken: mode === "user" ? token ?? undefined : undefined });
    }
}

/**
 * Safety-net drain, called from flush() (periodic timer + visibilitychange
 * hidden) so pre-hydration events aren't stranded if the tab tears down
 * before the hydration watcher ever fires. Deliberately narrower than
 * flushPreHydrationBuffer(): it only ever tags as 'user', and only when a
 * token is actually present right now — a real token can only belong to the
 * real user, hydrated or not, so that send is always safe. If no token is
 * present we do NOT guess 'guest' here (hydration may still be pending and
 * the visitor may in fact be logged in); the buffer is left untouched and
 * either drains correctly once hydration completes, or — only in the true
 * teardown-before-hydration-with-no-token case — is lost. That bounded
 * residual loss is the accepted trade-off; it's strictly preferable to
 * misattributing a logged-in user's events to a minted guest session.
 */
function drainPreHydrationBufferAsUserIfPossible(): void {
    if (pendingPreHydrationEvents.length === 0) return;

    const { token } = useAuthStore.getState();
    if (!token) return;

    const events = pendingPreHydrationEvents;
    pendingPreHydrationEvents = [];
    sendEvents(events, { Authorization: `Bearer ${token}` });
}

/**
 * Subscribes (once) to authStore so the pre-hydration buffer drains the
 * moment `isHydrated` flips true, rather than waiting for the next
 * trackBackendEvent call (which may never come if the user leaves the page
 * with no further interaction).
 */
function ensureHydrationWatcher(): void {
    if (hydrationWatcherStarted || typeof window === "undefined") return;
    hydrationWatcherStarted = true;

    const unsubscribe = useAuthStore.subscribe((state) => {
        if (!state.isHydrated) return;
        unsubscribe();
        flushPreHydrationBuffer();
    });
}

function ensureListeners(): void {
    if (listenersInitialized || typeof document === "undefined") return;
    listenersInitialized = true;
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") flush();
    });
}

function ensureFlushTimer(): void {
    if (flushTimer || typeof window === "undefined") return;
    flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);
}

function enqueue(item: QueuedEvent): void {
    queue.push(item);
    ensureFlushTimer();
    ensureListeners();
    if (queue.length >= FLUSH_QUEUE_SIZE) {
        flush();
    }
}

/** POSTs a batch under the given auth header. Fire-and-forget: errors are swallowed. */
function sendEvents(events: BackendEvent[], authHeader: Record<string, string>): void {
    fetch(`${API_URL}/events`, {
        method: "POST",
        keepalive: true,
        headers: {
            "Content-Type": "application/json",
            ...authHeader,
        },
        // Batch-level platform: every event this tracker emits comes from the web app,
        // so it is stamped once here rather than repeated on each queued event.
        body: JSON.stringify({ events, platform: "web" }),
    }).catch(() => {});
}

/** Mints/reuses a guest session then sends. Drops silently if no session can be established. */
function flushGuestEvents(events: BackendEvent[]): void {
    ensureGuestSession()
        .then((sessionId) => {
            if (!sessionId) return;
            sendEvents(events, { "X-Session-Id": sessionId });
        })
        .catch(() => {});
}

/**
 * Fire-and-forget flush of whatever is currently queued. Never throws, never
 * retries. Events were tagged 'user' | 'guest' at enqueue time (based on
 * token presence then, to avoid cross-attribution); the two groups flush as
 * separate requests since they use different auth headers.
 *
 * 'user' events are further grouped by the token stamped on them at enqueue
 * time (QueuedEvent.userToken) and only the group matching the CURRENT token
 * is sent — any group stamped with a different (stale) token is dropped
 * silently. Without this, a user who logs out and a different user who logs
 * in within the same flush window (~5s / 20 events) would have the first
 * user's queued events sent under the second user's Bearer token, misattributing
 * their browsing to the wrong account. The 'user' group is dropped entirely
 * if there's no token at all at flush time (existing semantics); the 'guest'
 * group is dropped if no session can be established.
 *
 * Also runs the pre-hydration safety-net drain on every call (including the
 * visibilitychange-hidden teardown path), so events buffered before
 * hydration finished aren't stranded if the tab closes before the hydration
 * watcher fires — see drainPreHydrationBufferAsUserIfPossible().
 */
function flush(): void {
    drainPreHydrationBufferAsUserIfPossible();

    if (queue.length === 0) return;

    const batch = queue.slice(0, MAX_BATCH_SIZE);
    queue = queue.slice(MAX_BATCH_SIZE);

    const userEventsByToken = new Map<string, BackendEvent[]>();
    const guestEvents: BackendEvent[] = [];
    for (const item of batch) {
        if (item.mode === "user") {
            if (!item.userToken) continue; // no identity stamp — can't safely attribute, drop
            const group = userEventsByToken.get(item.userToken);
            if (group) {
                group.push(item.event);
            } else {
                userEventsByToken.set(item.userToken, [item.event]);
            }
        } else {
            guestEvents.push(item.event);
        }
    }

    const { token } = useAuthStore.getState();

    if (token) {
        const currentUserEvents = userEventsByToken.get(token);
        if (currentUserEvents && currentUserEvents.length > 0) {
            sendEvents(currentUserEvents, { Authorization: `Bearer ${token}` });
        }
    }
    // else: token missing/gone at flush time — drop all 'user' groups silently.
    // Any group whose stamped token doesn't match the current token is also
    // dropped (never sent) since it belongs to a different account.

    if (guestEvents.length > 0) {
        flushGuestEvents(guestEvents);
    }

    if (token) {
        linkGuestSessionIfNeeded(token);
    }
}

const CHANNELS = new Set(["storefront", "payment_link", "external_link"]);

/**
 * A GTM event as the backend event it becomes (without occurred_at), or null when it is not tracked.
 * Planner storefront / payment link / a venue vendor's /book-external link: tag the channel (and the
 * vendor) so analytics can tell these events from marketplace ones. Untagged events are marketplace.
 * On the external link, the remembered vendor referral code rides along as referrer_code.
 */
export function toBackendEvent(gtmEvent: GTMEventPayload): Omit<BackendEvent, "occurred_at"> | null {
    const backendType = GTM_TO_BACKEND_EVENT[gtmEvent?.event];
    if (!backendType) return null;
    const mapped = mapToBackendEvent(backendType, gtmEvent);
    if (!mapped) return null;
    if (CHANNELS.has(gtmEvent.channel)) {
        mapped.metadata = {
            ...(mapped.metadata || {}),
            channel: gtmEvent.channel,
            ...(gtmEvent.vendor_id && !mapped.metadata?.vendor_id ? { vendor_id: String(gtmEvent.vendor_id) } : {}),
            ...(gtmEvent.channel === "external_link" && gtmEvent.referrer_code ? { referrer_code: String(gtmEvent.referrer_code) } : {}),
        };
    }
    return mapped;
}

/**
 * Entry point called from GTMManager.pushEvent() for every GTM event.
 * Guests are tracked too: with no token the event still enqueues, tagged
 * 'guest' so flush() sends it under a minted session instead of dropping it.
 * Still bails for unmapped events or events that can't produce a required
 * entity_id.
 *
 * Mode is only decided once authStore has finished rehydrating — until then
 * `token` isn't trustworthy (a logged-in user's first render can see it as
 * null), so the event holds in `pendingPreHydrationEvents` and gets tagged
 * once `isHydrated` flips, rather than risking a premature 'guest' tag that
 * would mint a real guest session for an authenticated cold load.
 */
export function trackBackendEvent(gtmEvent: GTMEventPayload): void {
    if (typeof window === "undefined") return;

    const mapped = toBackendEvent(gtmEvent);
    if (!mapped) return;

    const event: BackendEvent = { ...mapped, occurred_at: new Date().toISOString() };

    const { token, isHydrated } = useAuthStore.getState();
    if (!isHydrated) {
        pendingPreHydrationEvents.push(event);
        if (pendingPreHydrationEvents.length > PRE_HYDRATION_BUFFER_CAP) {
            pendingPreHydrationEvents.shift(); // drop-oldest, see buffer's declaration comment
        }
        // Arm the same timer/listener the main queue relies on so this
        // buffer isn't stranded if hydration never gets a chance to fire
        // before the tab tears down — flush() drains it defensively too.
        ensureFlushTimer();
        ensureListeners();
        ensureHydrationWatcher();
        return;
    }

    const mode: EventMode = token ? "user" : "guest";
    enqueue({ event, mode, userToken: mode === "user" ? token ?? undefined : undefined });
}
